import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useGpsTracker from "@/components/vi/GpsTracker";
import { ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/api";
import {
  LayoutDashboard, Users2, Target, FileText, FileSignature,
  FolderKanban, ListTodo, CalendarDays, UserCog, Clock, Plane, FolderOpen,
  BarChart3, Bell, BellRing, Settings, Search, LogOut, Menu, X, ChevronDown,
  Wallet, HandCoins, KeyRound, Building2, ReceiptText, Package, ArrowLeftRight, Warehouse, Truck, ShoppingCart, ClipboardList, PackageCheck, ChevronsDownUp, ChevronsUpDown,
  Receipt, Landmark, ShieldCheck, PiggyBank, Wrench, Calculator, BookOpen, ListOrdered, CalendarRange, Percent, Sparkles, FileBarChart, Scale, FileCheck, ClipboardCheck,
  Mail, PhoneCall, UserCheck, MessageSquare, Megaphone, HardHat, Coins,
  LifeBuoy, Link2, Bike, Route, Map as MapIcon, MapPin, Lightbulb, TrendingUp,
  AlertTriangle, Gauge, PenSquare, Inbox, SendHorizontal, FileStack, SlidersHorizontal, Construction, Globe,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { InstallAppButton } from "@/components/InstallAppButton";
import { InstallBanner } from "@/components/InstallBanner";
import { PermGuard } from "@/components/PermGuard";
import { AccountSecurityDialog } from "@/components/AccountSecurityDialog";
import { enablePush } from "@/lib/push";
import { ROUTE_MODULE, permCan } from "@/lib/constants";

const NAV = [
  { section: "Pilotage", items: [
    { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: "all" },
  ]},
  { section: "Commercial", items: [
    { to: "/prospects", label: "Prospects", icon: Users2, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/clients", label: "Clients", icon: Building2, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/projets-clients", label: "Projets clients", icon: FolderKanban, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/opportunites", label: "Opportunités", icon: Target, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/devis", label: "Devis", icon: FileText, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/prestations", label: "Prestations", icon: Wrench, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/artisans", label: "Artisans", icon: Wrench, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/factures", label: "Factures", icon: ReceiptText, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/contrats", label: "Contrats", icon: FileSignature, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
    { to: "/modeles-contrats", label: "Modèles de contrats", icon: FileText, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/livraisons", label: "Livraisons", icon: PackageCheck, roles: ["super_admin","direction_generale","direction_commerciale","commercial","responsable"] },
  ]},
  { section: "Stock & Achats", items: [
    { to: "/articles", label: "Articles", icon: Package, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/mouvements-stock", label: "Mouvements", icon: ArrowLeftRight, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/achats", label: "Achats", icon: ShoppingCart, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/commandes", label: "Commandes", icon: ClipboardList, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/inventaire", label: "Inventaire", icon: ClipboardList, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/magasins", label: "Magasins", icon: Warehouse, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/fournisseurs", label: "Fournisseurs", icon: Truck, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
  ]},
  { section: "Technique", items: [
    { to: "/technique", label: "Tableau de bord technique", icon: Construction, roles: "all", since: "2026-10-08" },
    { to: "/technique/suivi", label: "Suivi technique", icon: ClipboardCheck, roles: "all", since: "2026-10-08" },
  ]},
  { section: "Portail client", items: [
    { to: "/portail-admin", label: "Portail client", icon: Globe, roles: "all", since: "2026-10-08" },
  ]},
  { section: "Projets internes", items: [
    { to: "/projets", label: "Projets internes", icon: FolderKanban, roles: "all" },
    { to: "/taches", label: "Tâches", icon: ListTodo, roles: "all" },
    { to: "/agenda", label: "Agenda", icon: CalendarDays, roles: "all" },
  ]},
  { section: "Collaborateurs & Temps", items: [
    { to: "/collaborateurs", label: "Collaborateurs", icon: UserCog, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/ouvriers", label: "Ouvriers", icon: HardHat, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/rapports-collaborateurs", label: "Rapports d'activité", icon: ClipboardList, roles: "all" },
    { to: "/pointage", label: "Pointage", icon: Clock, roles: "all" },
    { to: "/feuilles-temps", label: "Feuilles de temps", icon: FileText, roles: "all" },
    { to: "/conges", label: "Congés & Absences", icon: Plane, roles: "all" },
  ]},
  { section: "Trésorerie", items: [
    { to: "/tresorerie", label: "Caisses & Banques", icon: Wallet, roles: ["super_admin","direction_generale","caissier","responsable"] },
    { to: "/demandes-fonds", label: "Demandes de fonds", icon: HandCoins, roles: "all" },
  ]},
  { section: "Finance / Compta", items: [
    { to: "/finance", label: "Tableau de bord", icon: PiggyBank, roles: ["super_admin","direction_generale","caissier"] },
    { to: "/charges", label: "Charges", icon: Receipt, roles: ["super_admin","direction_generale","caissier"] },
    { to: "/impots", label: "Impôts & taxes", icon: Landmark, roles: ["super_admin","direction_generale","caissier"] },
    { to: "/cotisations", label: "Cotisations sociales", icon: ShieldCheck, roles: ["super_admin","direction_generale","caissier"] },
    { to: "/creances", label: "Créances", icon: Coins, roles: ["super_admin","direction_generale","direction_commerciale","caissier","comptable"] },
    { to: "/dettes", label: "Dettes", icon: HandCoins, roles: ["super_admin","direction_generale","caissier","comptable"] },
  ]},
  { section: "Comptabilité", items: [
    { to: "/comptabilite", label: "Tableau de bord", icon: Calculator, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/ecritures", label: "Écritures", icon: BookOpen, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/generation-comptable", label: "Génération auto", icon: Sparkles, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/mapping-comptable", label: "Mapping comptable", icon: Link2, roles: ["super_admin","direction_generale","comptable"], since: "2026-09-24" },
    { to: "/etats-comptables", label: "États comptables", icon: FileBarChart, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/declaration-tva", label: "Déclaration TVA", icon: FileCheck, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/lettrage", label: "Lettrage", icon: ClipboardCheck, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/rapprochement", label: "Rapprochement bancaire", icon: Scale, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/immobilisations", label: "Immobilisations", icon: Building2, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/plan-comptable", label: "Plan comptable", icon: ListOrdered, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/journaux", label: "Journaux", icon: FileText, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/exercices", label: "Exercices", icon: CalendarRange, roles: ["super_admin","direction_generale","comptable"] },
    { to: "/tva", label: "TVA & taux", icon: Percent, roles: ["super_admin","direction_generale","comptable"] },
  ]},
  { section: "RH — Paie", items: [
    { to: "/paie", label: "Tableau de bord", icon: BarChart3, roles: ["super_admin","direction_generale"] },
    { to: "/contrats-travail", label: "Contrats de travail", icon: FileSignature, roles: ["super_admin","direction_generale"] },
    { to: "/elements-paie", label: "Éléments de paie", icon: HandCoins, roles: ["super_admin","direction_generale"] },
    { to: "/bulletins", label: "Bulletins de paie", icon: ReceiptText, roles: ["super_admin","direction_generale"] },
  ]},
  { section: "Vente itinérante", items: [
    { to: "/vi", label: "Tableau de bord", icon: LayoutDashboard, roles: "all", since: "2026-10-07" },
    { to: "/vi/ma-tournee", label: "Ma tournée", icon: Bike, roles: "all" },
    { to: "/vi/tournees", label: "Tournées", icon: Route, roles: "all" },
    { to: "/vi/carte", label: "Carte des tricycles", icon: MapIcon, roles: ["super_admin", "direction_generale", "direction_commerciale", "responsable", "comptable"], strictRoles: true },
    { to: "/vi/alertes-gps", label: "Alertes GPS", icon: BellRing, roles: ["super_admin", "direction_generale", "direction_commerciale", "responsable", "comptable"], strictRoles: true },
    { to: "/vi/tricycles", label: "Tricycles", icon: Bike, roles: "all" },
    { to: "/vi/circuits", label: "Circuits & zones", icon: MapIcon, roles: "all" },
    { to: "/vi/clients", label: "Boutiques / Clients", icon: Building2, roles: "all" },
    { to: "/vi/stock", label: "Stock mobile", icon: Package, roles: "all" },
    { to: "/vi/a-visiter", label: "Clients à visiter", icon: Lightbulb, roles: "all", since: "2026-10-07" },
    { to: "/vi/ventes", label: "Ventes mobiles", icon: ShoppingCart, roles: "all" },
    { to: "/vi/conflits", label: "Conflits de synchro", icon: AlertTriangle, roles: "all" },
    { to: "/vi/visites", label: "Visites", icon: MapPin, roles: "all", since: "2026-10-07" },
    { to: "/vi/commandes", label: "Commandes", icon: ClipboardList, roles: "all", since: "2026-10-07" },
    { to: "/vi/devis", label: "Devis terrain", icon: FileText, roles: "all", since: "2026-10-07" },
    { to: "/vi/encaissements", label: "Encaissements", icon: Wallet, roles: "all" },
    { to: "/vi/retours", label: "Retours", icon: ArrowLeftRight, roles: "all" },
    { to: "/vi/depenses", label: "Dépenses de tournée", icon: Receipt, roles: "all" },
    { to: "/vi/inventaires", label: "Inventaires", icon: ClipboardCheck, roles: "all" },
    { to: "/vi/rapports", label: "Rapports", icon: FileBarChart, roles: "all" },
    { to: "/vi/objectifs", label: "Objectifs", icon: Target, roles: "all", since: "2026-10-07" },
    { to: "/vi/commissions", label: "Commissions", icon: Percent, roles: "all", since: "2026-10-07" },
    { to: "/vi/rentabilite", label: "Rentabilité", icon: TrendingUp, roles: ["super_admin", "direction_generale", "direction_commerciale", "responsable", "comptable"], since: "2026-10-07" },
    { to: "/vi/rapport-km", label: "Rapport kilomètres", icon: Gauge, roles: ["super_admin", "direction_generale", "direction_commerciale", "responsable", "comptable"], strictRoles: true },
    { to: "/vi/parametres", label: "Paramètres", icon: Settings, roles: ["super_admin", "direction_generale", "direction_commerciale"] },
  ]},
  { section: "Ressources", items: [
    { to: "/formation", label: "Formation & Guides", icon: BookOpen, roles: "all", since: "2026-09-24" },
    { to: "/documents", label: "Documents", icon: FolderOpen, roles: "all" },
    { to: "/rapports", label: "Rapports", icon: BarChart3, roles: ["super_admin","direction_generale","direction_commerciale","responsable"] },
    { to: "/notifications", label: "Notifications", icon: Bell, roles: "all" },
    { to: "/support", label: "Support", icon: LifeBuoy, roles: "all", since: "2026-09-24" },
  ]},
  { section: "Validation", collapsible: true, items: [
    { to: "/validations", label: "Tableau de bord", icon: LayoutDashboard, roles: "all", since: "2026-10-13" },
    { to: "/validations/en-attente", label: "Mes validations en attente", icon: ClipboardCheck, roles: "all" },
    { to: "/validations/effectuees", label: "Validations effectuées", icon: FileCheck, roles: "all" },
    { to: "/validations/mes-demandes", label: "Mes demandes soumises", icon: SendHorizontal, roles: "all", need: "submit" },
    { to: "/validations/toutes", label: "Toutes les validations", icon: ListOrdered, roles: "all", need: "view_all" },
    { to: "/validations/historique", label: "Historique", icon: CalendarRange, roles: "all", need: "history" },
    { to: "/validations/circuits", label: "Circuits de validation", icon: SlidersHorizontal, roles: "all", need: "configure" },
  ]},
  { section: "Partenariat", collapsible: true, items: [
    { to: "/partenariat", label: "Tableau de bord", icon: LayoutDashboard, roles: "all", need: "reports", since: "2026-10-13" },
    { to: "/partenariat/contacts", label: "Contacts à convertir", icon: UserCheck, roles: "all" },
    { to: "/partenariat/partenaires", label: "Tous les partenaires", icon: Users2, roles: "all" },
    { to: "/partenariat/pipelines", label: "Pipelines", icon: FolderKanban, roles: "all" },
    { to: "/partenariat/actions", label: "Actions et activités", icon: ListTodo, roles: "all" },
    { to: "/partenariat/relances", label: "Tâches et relances", icon: BellRing, roles: "all" },
    { to: "/partenariat/reunions", label: "Réunions et rendez-vous", icon: CalendarDays, roles: "all" },
    { to: "/partenariat/contrats", label: "Contrats et conventions", icon: FileSignature, roles: "all", need: "contracts" },
    { to: "/partenariat/opportunites", label: "Opportunités", icon: Target, roles: "all" },
    { to: "/partenariat/rapports", label: "Rapports et statistiques", icon: BarChart3, roles: "all", need: "reports" },
    { to: "/partenariat/parametres", label: "Paramètres du module", icon: Settings, roles: "all", need: "configure" },
  ]},
  { section: "Courriers", items: [
    { to: "/courriers/nouveau", label: "Créer un courrier", icon: PenSquare, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/courriers", label: "Liste des courriers", icon: Mail, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/courrier/recus", label: "Courriers reçus", icon: Inbox, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/courrier/envoyes", label: "Courriers envoyés", icon: SendHorizontal, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/courriers/modeles", label: "Modèles de courriers", icon: FileStack, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/courriers/parametres", label: "Paramètres courriers", icon: SlidersHorizontal, roles: ["super_admin","direction_generale"] },
  ]},
  { section: "Administration", items: [
    { to: "/messagerie", label: "Messagerie interne", icon: MessageSquare, roles: "all" },
    { to: "/campagnes", label: "Campagnes", icon: Megaphone, roles: ["super_admin","direction_generale"] },
    { to: "/appels", label: "Journal d'appels", icon: PhoneCall, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/visiteurs", label: "Visiteurs", icon: UserCheck, roles: ["super_admin","direction_generale","responsable"] },
    { to: "/utilisateurs", label: "Utilisateurs", icon: Users2, roles: ["super_admin","direction_generale"] },
    { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["super_admin","direction_generale"] },
  ]},
];

const allowed = (item, role, perms) => {
  const mod = ROUTE_MODULE[item.to] || (item.to.startsWith("/validations") ? "validation" : item.to.startsWith("/partenariat") ? "partenariat" : undefined);
  if (mod && item.need) return permCan(perms, mod, "read") && permCan(perms, mod, item.need);
  if (mod && item.strictRoles) return permCan(perms, mod, "read") && item.roles.includes(role);
  if (mod) return permCan(perms, mod, "read");
  return item.roles === "all" || item.roles.includes(role);
};

const isNew = (since) => {
  if (!since) return false;
  const d = new Date(since);
  return !isNaN(d.getTime()) && (Date.now() - d.getTime()) < 7 * 24 * 3600 * 1000;
};

const matchItem = (to, path) => (to === "/" ? path === "/" : path === to || path.startsWith(`${to}/`));
const fold = (s) => (s || "").split("").map((c) => c.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().slice(0, 1) || c).join("");

function Hl({ text, q }) {
  const i = q ? fold(text).indexOf(q) : -1;
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="rounded bg-amber-300/80 px-0.5 text-slate-900">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
}

function filterGroups(groups, q) {
  if (!q) return groups;
  return groups.map((g) => (fold(g.section).includes(q) ? g : { ...g, items: g.items.filter((i) => fold(i.label).includes(q)) })).filter((g) => g.items.length);
}

function MenuSearch({ value, onChange, onEnter }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      if (!ref.current || ref.current.offsetParent === null) return;
      e.preventDefault();
      ref.current.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="relative px-1 mb-1">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
      <input ref={ref} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Rechercher un menu…"
        onKeyDown={(e) => { if (e.key === "Escape") { onChange(""); e.currentTarget.blur(); } if (e.key === "Enter") onEnter(); }}
        className="w-full rounded-lg border border-white/10 bg-white/5 py-1.5 pl-8 pr-8 text-sm text-white placeholder:text-slate-500 focus:border-brand-500 focus:outline-none" data-testid="nav-search-input" />
      {value ? <button type="button" onClick={() => onChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" data-testid="nav-search-clear"><X className="h-3.5 w-3.5" /></button>
        : <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-white/10 px-1 text-[10px] text-slate-500">/</kbd>}
    </div>
  );
}

function useNavOpen(userId, activeSection, sections) {
  const key = `omnibat.nav.open.${userId || "anon"}`;
  const [open, setOpen] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(key)); if (s && typeof s === "object") return s; } catch { /* état absent */ }
    return activeSection ? { [activeSection]: true } : {};
  });
  useEffect(() => { if (activeSection) setOpen((o) => (o[activeSection] ? o : { ...o, [activeSection]: true })); }, [activeSection]);
  useEffect(() => { localStorage.setItem(key, JSON.stringify(open)); }, [key, open]);
  const toggle = (s) => setOpen((o) => ({ ...o, [s]: !o[s] }));
  const setAll = (v) => setOpen(Object.fromEntries(sections.map((s) => [s, v || s === activeSection])));
  return [open, toggle, setAll];
}

function SidebarContent({ role, onNavigate }) {
  const { permissions, user } = useAuth();
  const { pathname } = useLocation();
  const groups = NAV.map((grp) => ({ ...grp, items: grp.items.filter((i) => allowed(i, role, permissions)) })).filter((g) => g.items.length);
  const active = groups.find((g) => g.items.some((i) => matchItem(i.to, pathname)))?.section;
  const [open, toggle, setAll] = useNavOpen(user?.id, active, groups.map((g) => g.section));
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const q = fold(query.trim());
  const shown = filterGroups(groups, q);
  const goFirst = () => { const it = shown[0]?.items[0]; if (it) { navigate(it.to); setQuery(""); onNavigate?.(); } };
  return (
    <nav className="flex flex-col gap-2 px-3 py-4">
      <MenuSearch value={query} onChange={setQuery} onEnter={goFirst} />
      {!q && <div className="flex justify-end gap-1 px-1 -mt-1 mb-1">
        <button type="button" onClick={() => setAll(true)} title="Tout déplier" className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] text-slate-400 hover:bg-white/5 hover:text-white" data-testid="nav-expand-all"><ChevronsUpDown className="h-3.5 w-3.5" />Tout déplier</button>
        <button type="button" onClick={() => setAll(false)} title="Tout replier" className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] text-slate-400 hover:bg-white/5 hover:text-white" data-testid="nav-collapse-all"><ChevronsDownUp className="h-3.5 w-3.5" />Tout replier</button>
      </div>}
      {q && shown.length === 0 && <p className="px-3 py-2 text-xs text-slate-500" data-testid="nav-search-empty">Aucun menu ne correspond à « {query.trim()} ».</p>}
      {shown.map((grp) => {
        const items = grp.items;
        const isOpen = !!q || !!open[grp.section];
        const slug = grp.section.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");
        return (
          <div key={grp.section}>
            <button type="button" onClick={() => toggle(grp.section)} aria-expanded={isOpen}
              className={`w-full flex items-center justify-between rounded-md px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition-colors hover:bg-white/5 hover:text-white ${grp.section === active ? "text-emerald-300" : "text-slate-500"}`}
              data-testid={`nav-section-${slug}`}>
              <span className="truncate"><Hl text={grp.section} q={q} /></span>
              <span className="flex items-center gap-1.5">{!isOpen && <span className="rounded-full bg-white/10 px-1.5 text-[9px] text-slate-400">{items.length}</span>}<ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} /></span>
            </button>
            <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`} data-testid={`nav-group-${slug}`} aria-hidden={!isOpen}>
            <div className="overflow-hidden"><div className="flex flex-col gap-0.5 pt-1 pb-2">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/" || items.some((o) => o.to !== it.to && o.to.startsWith(`${it.to}/`))}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={() => { setQuery(""); onNavigate?.(); }}
                  data-testid={`nav-${it.to.replace("/","") || "dashboard"}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? "bg-brand-600 text-white shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <it.icon className="h-[18px] w-[18px] shrink-0" />
                  <span className="truncate"><Hl text={it.label} q={q} /></span>
                  {isNew(it.since) && <span data-testid={`nav-new-badge-${it.to.replace("/", "")}`} className="ml-auto rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Nouveau</span>}
                </NavLink>
              ))}
            </div></div></div>
          </div>
        );
      })}
    </nav>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 2) { setRes([]); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setRes(r.data.results); setOpen(true); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Rechercher prospect, projet, devis…"
        className="pl-9 bg-slate-50 border-slate-200 h-10"
      />
      {open && res.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg max-h-80 overflow-auto">
          {res.map((r) => (
            <button
              key={r.type + r.id}
              onMouseDown={() => { nav(r.link); setOpen(false); setQ(""); }}
              className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 text-left"
            >
              <span className="truncate">{r.label}</span>
              <span className="text-[10px] uppercase font-mono text-slate-400 ml-2">{r.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, role, logout, mustChange, clearMustChange } = useAuth();
  useGpsTracker(user?.role);
  const enablePushNotif = async () => {
    try { await enablePush(); toast.success("Notifications push activées"); }
    catch (e) { toast.error(e.message || "Échec de l'activation"); }
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [secOpen, setSecOpen] = useState(false);
  const nav = useNavigate();
  const menuRef = useRef();
  const notifRef = useRef();
  const seenRef = useRef(null);

  useEffect(() => {
    let stop = false;
    const poll = () => {
      api.get("/notifications").then((r) => {
        if (stop) return;
        const list = r.data;
        setNotifs(list);
        setUnread(list.filter((n) => !n.read).length);
        const ids = new Set(list.map((n) => n.id));
        if (seenRef.current === null) {
          seenRef.current = ids;
        } else {
          const fresh = list.filter((n) => !seenRef.current.has(n.id) && !n.read);
          fresh.slice(0, 3).forEach((n) => toast.info(n.message, { description: "Nouvelle notification" }));
          seenRef.current = ids;
        }
      }).catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openNotif = async (n) => {
    if (!n.read) { try { await api.patch(`/notifications/${n.id}/read`); } catch { /* non bloquant */ } setUnread((u) => Math.max(0, u - 1)); setNotifs((l) => l.map((x) => (x.id === n.id ? { ...x, read: true } : x))); }
    setNotifOpen(false);
    if (n.link) nav(n.link);
  };
  const markAll = async () => { try { await api.patch("/notifications/read-all"); } catch { /* non bloquant */ } setNotifs((l) => l.map((x) => ({ ...x, read: true }))); setUnread(0); };

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-100">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 blueprint-bg border-r border-slate-800 overflow-y-auto scrollbar-thin">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800 shrink-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shrink-0"><img src="/logo-omnibat.png" alt="OMNIBAT ONE" className="h-9 w-9 object-contain" /></div>
          <div>
            <p className="font-head font-extrabold text-white leading-none tracking-tight">OMNIBAT<span className="text-emerald-400"> ONE</span></p>
            <p className="text-[10px] text-slate-400 font-mono mt-0.5">Gestion & Pilotage</p>
          </div>
        </div>
        <SidebarContent role={role} />
      </aside>

      {/* Mobile drawer (self-managed, inline) */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 blueprint-bg border-r border-slate-800 overflow-y-auto scrollbar-thin animate-fadeup">
            <div className="flex items-center justify-between px-5 h-16 border-b border-slate-800">
              <div className="flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white shrink-0"><img src="/logo-omnibat.png" alt="OMNIBAT ONE" className="h-9 w-9 object-contain" /></div>
                <p className="font-head font-extrabold text-white tracking-tight">OMNIBAT<span className="text-emerald-400"> ONE</span></p>
              </div>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-close"><X className="h-5 w-5 text-slate-300" /></button>
            </div>
            <SidebarContent role={role} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b bg-white px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6 text-slate-700" />
          </button>
          <div className="flex-1 hidden sm:block"><GlobalSearch /></div>
          <div className="flex-1 sm:hidden" />

          <InstallAppButton />

          <button onClick={enablePushNotif} title="Activer les notifications push" data-testid="enable-push-btn" className="rounded-full p-2 hover:bg-slate-100">
            <BellRing className="h-5 w-5 text-slate-600" />
          </button>

          <div className="relative" ref={notifRef}>
            <button onClick={() => setNotifOpen((o) => !o)} data-testid="notif-bell" className="relative rounded-full p-2 hover:bg-slate-100">
              <Bell className="h-5 w-5 text-slate-600" />
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{unread}</span>}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 rounded-lg border bg-white shadow-lg z-50 overflow-hidden" data-testid="notif-panel">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <p className="font-head font-semibold text-sm">Notifications</p>
                  {unread > 0 && <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-brand-600 hover:underline">Tout lire</button>}
                </div>
                <div className="max-h-96 overflow-auto">
                  {notifs.length === 0 ? (
                    <p className="px-3 py-6 text-center text-sm text-slate-400">Aucune notification</p>
                  ) : notifs.slice(0, 10).map((n) => (
                    <button key={n.id} onClick={() => openNotif(n)} data-testid={`notif-item-${n.id}`}
                      className={`flex w-full items-start gap-2 px-3 py-2.5 text-left border-b last:border-0 hover:bg-slate-50 ${n.read ? "" : "bg-brand-50/50"}`}>
                      <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-slate-300" : "bg-brand-500"}`} />
                      <span className="flex-1 min-w-0"><span className={`block text-sm truncate ${n.read ? "text-slate-600" : "font-semibold text-slate-800"}`}>{n.message}</span></span>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setNotifOpen(false); nav("/notifications"); }} className="block w-full px-3 py-2 text-center text-sm font-semibold text-brand-600 border-t hover:bg-slate-50">Voir toutes les notifications</button>
              </div>
            )}
          </div>

          <div className="relative" ref={menuRef}>
            <button data-testid="user-menu" onClick={() => setMenuOpen((o) => !o)} className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-slate-100">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1C3144] text-white text-xs font-bold">{initials}</div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold leading-none text-slate-800">{user?.name}</p>
                <p className="text-[11px] text-slate-500">{ROLE_LABELS[role]}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400 hidden md:block" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg border bg-white shadow-lg z-50 py-1" data-testid="user-menu-panel">
                <div className="px-3 py-2 border-b">
                  <p className="font-semibold text-sm">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{ROLE_LABELS[role]}</p>
                </div>
                <button onClick={() => { setMenuOpen(false); setSecOpen(true); }} data-testid="security-btn" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <KeyRound className="h-4 w-4" /> Sécurité — Mot de passe
                </button>
                <button onClick={logout} data-testid="logout-btn" className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50">
                  <LogOut className="h-4 w-4" /> Se déconnecter
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="p-4 sm:p-6 max-w-[1600px] mx-auto animate-fadeup"><InstallBanner /><PermGuard />{children}</main>
      </div>
      <AccountSecurityDialog open={secOpen} onOpenChange={setSecOpen} />
      <AccountSecurityDialog open={!!mustChange} forced onOpenChange={() => {}} onSuccess={clearMustChange} />
    </div>
  );
}
